import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import * as queryKeys from '@data/constants/keys';
import { fetchProductStore, writeProductStore } from '@data/api/products';

import { loadProductStore, onProductStoreSaveFailed } from './productStore';

/** Read afresh on every opening and never in the background: a refetch mid-edit would put back what was just typed over. */
export const productStoreQuery = (productId: string, readOnly = false) => ({
  queryKey: [queryKeys.PRODUCT_STORE, productId, readOnly],
  queryFn: () =>
    loadProductStore(productId, { fetch: fetchProductStore, write: readOnly ? undefined : writeProductStore }),
  gcTime: 0,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: 1,
});

/** `revision` moves when a refused save made the page reload what the API really kept, so the tabs remount on it. */
export const useProductStore = (
  productId: string,
  /** A client's: the API refuses their writes, so none is sent. */
  readOnly = false,
): { isReady: boolean; isError: boolean; revision: number } => {
  const { isSuccess, isError, refetch } = useQuery({ ...productStoreQuery(productId, readOnly), enabled: !!productId });
  const [revision, setRevision] = useState(0);

  useEffect(
    () =>
      onProductStoreSaveFailed(failed => {
        if (failed !== productId) return;
        refetch().then(() => setRevision(current => current + 1));
      }),
    [productId, refetch],
  );

  return { isReady: isSuccess, isError, revision };
};
